import React, { useEffect, useMemo, useState, useCallback } from 'react';

/**
 * Trip Operations → Overdue Trips
 * Every trip run whose start time passed but was never started (or started and never completed),
 * for today and the past N days, with route, vehicle, driver photo/phone and what the driver did that day.
 * Backend: GET /api/overdue-trips/:operatorId?days=, POST /api/overdue-trips/resolve,
 *          POST /api/trip-alerts/action (nudge / contacted log)
 */

const RED = '#dc2626';

const REASONS = {
    driver_not_logged_in:         { label: 'Driver not logged in',        short: 'Not logged in',     color: '#b91c1c', bg: '#fee2e2' },
    driver_online_not_started:    { label: 'Driver online, not started',  short: 'Online, not started', color: '#c2410c', bg: '#ffedd5' },
    driver_logged_in_not_started: { label: 'Logged in, never started',    short: 'Logged in, not started', color: '#b45309', bg: '#fef3c7' },
    no_driver:                    { label: 'No driver assigned',          short: 'No driver',         color: '#7f1d1d', bg: '#fecaca' },
    not_completed:                { label: 'Started, not completed',      short: 'Not completed',     color: '#6d28d9', bg: '#ede9fe' },
};

const SEVERITY = {
    missed:   { label: 'Date passed', color: '#7f1d1d', bg: '#fecaca' },
    critical: { label: 'Critical',    color: '#b91c1c', bg: '#fee2e2' },
    overdue:  { label: 'Overdue',     color: '#c2410c', bg: '#ffedd5' },
    delayed:  { label: 'Delayed',     color: '#b45309', bg: '#fef3c7' },
};

const fmtDate = (ds) => {
    if (!ds) return '';
    const d = new Date(ds + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
};
const fmtTime = (v) => (v ? new Date(v).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '');
const fmtDateTime = (v) => (v ? new Date(v).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true }) : '—');
const initials = (name = '') => name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';

function Avatar({ driver, size = 44, online }) {
    const [broken, setBroken] = useState(false);
    const showImg = driver?.photo && !broken;
    return (
        <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
            {showImg ? (
                <img src={driver.photo} alt={driver.name} onError={() => setBroken(true)}
                    style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', border: '2px solid #fff', boxShadow: '0 0 0 1px #e2e8f0' }} />
            ) : (
                <div style={{
                    width: size, height: size, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: driver ? '#e0e7ff' : '#f1f5f9', color: driver ? '#3730a3' : '#94a3b8',
                    fontWeight: 800, fontSize: size * 0.36, border: '2px solid #fff', boxShadow: '0 0 0 1px #e2e8f0'
                }}>{driver ? initials(driver.name) : '?'}</div>
            )}
            {online !== undefined && (
                <span title={online ? 'Online now' : 'Offline'} style={{
                    position: 'absolute', right: 0, bottom: 0, width: size * 0.28, height: size * 0.28, borderRadius: '50%',
                    background: online ? '#22c55e' : '#94a3b8', border: '2px solid #fff'
                }} />
            )}
        </div>
    );
}

function Pill({ children, color, bg, title }) {
    return (
        <span title={title} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 999, fontSize: 11, fontWeight: 700, color, background: bg, whiteSpace: 'nowrap' }}>
            {children}
        </span>
    );
}

function DriverActivityPills({ row }) {
    const a = row.activity;
    if (!row.driver) return <Pill color="#7f1d1d" bg="#fecaca">No driver assigned</Pill>;
    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {a?.loggedInOnDate
                ? <Pill color="#166534" bg="#dcfce7">✓ Logged in {row.isToday ? 'today' : 'that day'}{a.firstLoginAt ? ` · ${fmtTime(a.firstLoginAt)}` : ''}</Pill>
                : <Pill color="#b91c1c" bg="#fee2e2">✕ Did not log in {row.isToday ? 'today' : 'that day'}</Pill>}
            {a?.loggedOutAt && <Pill color="#475569" bg="#f1f5f9">Logged out {fmtTime(a.loggedOutAt)}</Pill>}
            {a?.onlineNow
                ? <Pill color="#166534" bg="#dcfce7">● Online now</Pill>
                : <Pill color="#64748b" bg="#f1f5f9">○ Offline now</Pill>}
            {row.kind === 'not_started' && <Pill color="#b91c1c" bg="#fee2e2">Trip not started</Pill>}
            {!a?.hasApp && <Pill color="#92400e" bg="#fef3c7" title="No push token — driver can't receive app notifications">No app push</Pill>}
        </div>
    );
}

export default function OverdueTrips({ user }) {
    const isAdmin = user?.userType === 'admin';
    const operatorId = isAdmin ? 'all'
        : ((typeof user?.operatorId === 'object' ? user?.operatorId?._id : user?.operatorId) || user?._id || 'all');
    const actedBy = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || (user?.userType === 'operator' ? 'Operator' : 'Admin');

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [days, setDays] = useState(7);
    const [reason, setReason] = useState('');
    const [when, setWhen] = useState('all'); // all | today | past
    const [client, setClient] = useState('');
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState(null);
    const [busy, setBusy] = useState('');
    const [toast, setToast] = useState('');
    const [cancelFor, setCancelFor] = useState(null);
    const [cancelReason, setCancelReason] = useState('');

    const load = useCallback(async (silent) => {
        if (!silent) setLoading(true);
        try {
            const res = await fetch(`/api/overdue-trips/${operatorId}?days=${days}`, { cache: 'no-store' });
            const json = await res.json();
            if (!res.ok) throw new Error(json.message || 'Failed to load');
            setData(json);
            setError('');
        } catch (e) {
            setError(e.message || 'Failed to load overdue trips');
        } finally {
            setLoading(false);
        }
    }, [operatorId, days]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { const id = setInterval(() => load(true), 60000); return () => clearInterval(id); }, [load]);
    useEffect(() => { if (!toast) return; const id = setTimeout(() => setToast(''), 3000); return () => clearTimeout(id); }, [toast]);

    const trips = data?.trips || [];
    const clients = useMemo(() => [...new Set(trips.map(t => t.clientName).filter(Boolean))].sort(), [trips]);
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return trips.filter(t =>
            (!reason || t.reasonCode === reason) &&
            (when === 'all' || (when === 'today' ? t.isToday : !t.isToday)) &&
            (!client || t.clientName === client) &&
            (!q || [t.tripName, t.routeName, t.clientName, t.driver?.name, t.driver?.phone, t.vehicle?.number, t.pickupLocation, t.dropLocation]
                .filter(Boolean).some(v => v.toLowerCase().includes(q))));
    }, [trips, reason, when, client, search]);

    // keep the drawer in sync after refresh
    const sel = selected ? trips.find(t => t.key === selected) : null;

    const resolve = async (row, status, why = '') => {
        setBusy(`${row.key}:${status}`);
        try {
            const res = await fetch('/api/overdue-trips/resolve', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tripId: row.tripId, date: row.date, status, reason: why, actedBy })
            });
            const json = await res.json();
            if (!res.ok) throw new Error(json.message || 'Update failed');
            setToast(status === 'Completed' ? `Marked completed for ${fmtDate(row.date)}` : `Trip cancelled for ${fmtDate(row.date)}`);
            if (selected === row.key) setSelected(null);
            await load(true);
        } catch (e) {
            setToast(e.message);
        } finally { setBusy(''); }
    };

    const nudge = async (row) => {
        setBusy(`${row.key}:nudge`);
        try {
            const res = await fetch('/api/trip-alerts/action', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tripId: row.tripId, instanceId: row.instanceId || undefined, action: 'nudged', actedBy,
                    note: `Your trip "${row.tripName}" (${fmtDate(row.date)}, ${row.scheduledStart}) has not started. Please start it now or call operations.` })
            });
            const json = await res.json();
            if (!res.ok) throw new Error(json.message || 'Could not notify driver');
            setToast(`Reminder sent to ${row.driver?.name}`);
        } catch (e) { setToast(e.message); } finally { setBusy(''); }
    };

    const logCall = (row) => {
        fetch('/api/trip-alerts/action', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ tripId: row.tripId, instanceId: row.instanceId || undefined, action: 'contacted', actedBy, note: 'Called driver from Overdue Trips' })
        }).catch(() => {});
    };

    const s = data?.summary || {};
    const chips = [
        { k: '', label: 'All overdue', n: s.total || 0, color: RED, bg: '#fee2e2' },
        ...Object.entries(REASONS).map(([k, m]) => ({ k, label: m.label, n: { driver_not_logged_in: s.driverNotLoggedIn, driver_online_not_started: s.driverOnlineNotStarted, driver_logged_in_not_started: s.driverLoggedInNotStarted, no_driver: s.noDriver, not_completed: s.notCompleted }[k] || 0, color: m.color, bg: m.bg })),
    ];

    const btn = (bg, color = '#fff', border = 'none') => ({
        padding: '7px 12px', borderRadius: 8, border, background: bg, color, fontSize: 12, fontWeight: 700,
        cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none', whiteSpace: 'nowrap'
    });
    const selectStyle = { height: 40, padding: '0 12px', borderRadius: 10, border: '1px solid #e2e8f0', background: '#fff', fontSize: 13, fontWeight: 600, color: '#334155' };

    return (
        <div className="md-page">
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: RED, boxShadow: '0 0 0 4px #fee2e2' }} />
                        <h1 className="md-title" style={{ color: '#991b1b' }}>Overdue Trips</h1>
                    </div>
                    <p className="md-subtitle">Trips whose start time has passed but weren't started or completed, with what the driver did that day.</p>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <select value={days} onChange={e => setDays(Number(e.target.value))} style={selectStyle}>
                        <option value={1}>Today & yesterday</option>
                        <option value={7}>Last 7 days</option>
                        <option value={30}>Last 30 days</option>
                        <option value={90}>Last 90 days</option>
                    </select>
                    <button onClick={() => load()} style={btn('#fff', '#334155', '1px solid #e2e8f0')}>↻ Refresh</button>
                </div>
            </div>

            {/* Summary */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
                {chips.map(c => (
                    <button key={c.k || 'all'} onClick={() => setReason(reason === c.k ? '' : c.k)}
                        style={{
                            textAlign: 'left', padding: '14px 16px', borderRadius: 14, cursor: 'pointer',
                            border: `1.5px solid ${reason === c.k ? c.color : 'transparent'}`, background: c.bg,
                            boxShadow: reason === c.k ? `0 0 0 3px ${c.bg}` : 'none'
                        }}>
                        <div style={{ fontSize: 11, fontWeight: 800, color: c.color, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{c.label}</div>
                        <div style={{ fontSize: 26, fontWeight: 800, color: c.color, marginTop: 4 }}>{c.n}</div>
                    </button>
                ))}
            </div>

            {/* Filters + list */}
            <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #eef0f4', boxShadow: '0 1px 3px rgba(15,23,42,0.04)', overflow: 'hidden' }}>
                <div style={{ display: 'flex', gap: 10, padding: 16, flexWrap: 'wrap', alignItems: 'center', borderBottom: '1px solid #f1f5f9' }}>
                    <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search trip, route, driver, phone, vehicle…"
                        style={{ ...selectStyle, flex: '1 1 260px', fontWeight: 500 }} />
                    <select value={client} onChange={e => setClient(e.target.value)} style={selectStyle}>
                        <option value="">All clients</option>
                        {clients.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: 10, padding: 3 }}>
                        {[['all', `All (${s.total || 0})`], ['today', `Today (${s.today || 0})`], ['past', `Past dates (${s.past || 0})`]].map(([k, l]) => (
                            <button key={k} onClick={() => setWhen(k)} style={{
                                padding: '7px 12px', borderRadius: 8, border: 'none', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                                background: when === k ? '#fff' : 'transparent', color: when === k ? '#991b1b' : '#64748b',
                                boxShadow: when === k ? '0 1px 2px rgba(0,0,0,0.08)' : 'none'
                            }}>{l}</button>
                        ))}
                    </div>
                </div>

                {loading && !data ? (
                    <div style={{ padding: 48, textAlign: 'center', color: '#64748b' }}>Loading overdue trips…</div>
                ) : error ? (
                    <div style={{ padding: 48, textAlign: 'center', color: RED, fontWeight: 600 }}>{error}</div>
                ) : filtered.length === 0 ? (
                    <div style={{ padding: 56, textAlign: 'center' }}>
                        <div style={{ fontSize: 34 }}>✓</div>
                        <div style={{ fontWeight: 800, color: '#166534', marginTop: 6 }}>No overdue trips</div>
                        <div style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>Every trip in this range was started on time or closed.</div>
                    </div>
                ) : (
                    <div>
                        {filtered.map(row => {
                            const r = REASONS[row.reasonCode] || REASONS.driver_logged_in_not_started;
                            const sev = SEVERITY[row.severity] || SEVERITY.overdue;
                            return (
                                <div key={row.key} onClick={() => setSelected(row.key)}
                                    style={{
                                        display: 'grid', gridTemplateColumns: '150px minmax(220px, 1.3fr) minmax(240px, 1.4fr) auto', gap: 18,
                                        alignItems: 'center', padding: '16px 18px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer',
                                        borderLeft: `4px solid ${r.color}`, background: '#fff'
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.background = '#fffafa'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}>
                                    {/* When / how late */}
                                    <div>
                                        <div style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>{row.isToday ? 'Today' : fmtDate(row.date)}</div>
                                        <div style={{ fontSize: 20, fontWeight: 800, color: RED, lineHeight: 1.2, marginTop: 2 }}>{row.lateText}</div>
                                        <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>{row.kind === 'not_completed' ? 'past drop time' : 'overdue'}</div>
                                        <div style={{ marginTop: 6 }}><Pill color={sev.color} bg={sev.bg}>{sev.label}</Pill></div>
                                    </div>

                                    {/* Trip */}
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.tripName}</div>
                                        <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>
                                            {[row.clientName, row.routeName && `Route: ${row.routeName}`].filter(Boolean).join(' · ') || '—'}
                                        </div>
                                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                                            🕒 {row.scheduledStart}{row.scheduledEnd ? ` – ${row.scheduledEnd}` : ''} · {row.tripType} · {row.frequency}
                                        </div>
                                        {row.vehicle && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>🚐 {row.vehicle.number}{row.vehicle.model ? ` · ${row.vehicle.model}` : ''}</div>}
                                        <div style={{ marginTop: 8 }}><Pill color={r.color} bg={r.bg}>⚠ {row.reason}</Pill></div>
                                    </div>

                                    {/* Driver */}
                                    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', minWidth: 0 }}>
                                        <Avatar driver={row.driver} online={row.driver ? !!row.activity?.onlineNow : undefined} />
                                        <div style={{ minWidth: 0 }}>
                                            <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>{row.driver?.name || 'No driver assigned'}</div>
                                            {row.driver && (
                                                <div style={{ fontSize: 12, color: '#475569', marginTop: 1 }}>
                                                    {row.driver.code && <span>{row.driver.code} · </span>}
                                                    {row.driver.phone ? <a href={`tel:${row.driver.phoneDial}`} onClick={e => { e.stopPropagation(); logCall(row); }} style={{ color: '#1d4ed8', fontWeight: 700, textDecoration: 'none' }}>{row.driver.phone}</a> : 'No phone'}
                                                </div>
                                            )}
                                            <div style={{ marginTop: 6 }}><DriverActivityPills row={row} /></div>
                                        </div>
                                    </div>

                                    {/* Actions */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'stretch' }} onClick={e => e.stopPropagation()}>
                                        {row.driver?.phoneDial && (
                                            <a href={`tel:${row.driver.phoneDial}`} onClick={() => logCall(row)} style={btn('#fff', '#1d4ed8', '1px solid #bfdbfe')}>📞 Call driver</a>
                                        )}
                                        <button disabled={!!busy} onClick={() => resolve(row, 'Completed')} style={btn('#10b981')}>
                                            {busy === `${row.key}:Completed` ? 'Saving…' : '✓ Mark completed'}
                                        </button>
                                        <button onClick={() => setSelected(row.key)} style={btn('#fff', '#334155', '1px solid #e2e8f0')}>View details →</button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Detail drawer */}
            {sel && (
                <div onClick={() => setSelected(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.35)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }}>
                    <div onClick={e => e.stopPropagation()} style={{ width: 'min(520px, 100%)', height: '100%', background: '#f8fafc', overflowY: 'auto', boxShadow: '-12px 0 32px rgba(15,23,42,0.18)' }}>
                        <div style={{ background: 'linear-gradient(135deg, #991b1b, #dc2626)', color: '#fff', padding: '20px 22px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', opacity: 0.85 }}>Overdue trip · {sel.isToday ? 'Today' : fmtDate(sel.date)}</span>
                                <button onClick={() => setSelected(null)} style={{ background: 'rgba(255,255,255,0.18)', border: 'none', color: '#fff', width: 30, height: 30, borderRadius: 8, cursor: 'pointer', fontSize: 16 }}>✕</button>
                            </div>
                            <div style={{ fontSize: 20, fontWeight: 800, marginTop: 8 }}>{sel.tripName}</div>
                            <div style={{ fontSize: 13, opacity: 0.9, marginTop: 4 }}>{sel.reason}</div>
                            <div style={{ display: 'flex', gap: 22, marginTop: 14 }}>
                                <div><div style={{ fontSize: 24, fontWeight: 800 }}>{sel.lateText}</div><div style={{ fontSize: 11, opacity: 0.85 }}>{sel.kind === 'not_completed' ? 'past drop time' : 'late, not started'}</div></div>
                                <div><div style={{ fontSize: 24, fontWeight: 800 }}>{sel.scheduledStart}</div><div style={{ fontSize: 11, opacity: 0.85 }}>scheduled start</div></div>
                            </div>
                        </div>

                        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
                            {/* Driver */}
                            <Section title="Driver">
                                <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                                    <Avatar driver={sel.driver} size={64} online={sel.driver ? !!sel.activity?.onlineNow : undefined} />
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>{sel.driver?.name || 'No driver assigned'}</div>
                                        {sel.driver && <div style={{ fontSize: 13, color: '#475569', marginTop: 2 }}>{[sel.driver.code, sel.driver.phone].filter(Boolean).join(' · ')}</div>}
                                        {sel.driver?.status && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Employment: {sel.driver.status}</div>}
                                    </div>
                                </div>
                                {sel.driver && (
                                    <>
                                        <div style={{ marginTop: 12 }}><DriverActivityPills row={sel} /></div>
                                        <Grid rows={[
                                            [`Logged in ${sel.isToday ? 'today' : 'on ' + fmtDate(sel.date)}`, sel.activity?.loggedInOnDate ? `Yes · ${fmtTime(sel.activity.firstLoginAt) || '—'}` : 'No'],
                                            ['Logged out', sel.activity?.loggedOutAt ? fmtTime(sel.activity.loggedOutAt) : (sel.activity?.loggedInOnDate ? 'Still on duty' : '—')],
                                            ['Online right now', sel.activity?.onlineNow ? 'Yes' : 'No'],
                                            ['Last app login', fmtDateTime(sel.activity?.lastLoginAt)],
                                            ['Trip started', sel.startedAt ? fmtDateTime(sel.startedAt) : 'No'],
                                            ['Last GPS from trip', sel.lastGpsAt ? fmtDateTime(sel.lastGpsAt) : 'None'],
                                            ['App / device', [sel.activity?.appVersion && `v${sel.activity.appVersion}`, sel.activity?.device, sel.activity?.hasApp ? 'push on' : 'push off'].filter(Boolean).join(' · ') || '—'],
                                        ]} />
                                        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                                            {sel.driver.phoneDial && <a href={`tel:${sel.driver.phoneDial}`} onClick={() => logCall(sel)} style={btn('#1d4ed8')}>📞 Call</a>}
                                            {sel.driver.phoneDial && <a href={`https://wa.me/${sel.driver.phoneDial.replace('+', '')}`} target="_blank" rel="noreferrer" onClick={() => logCall(sel)} style={btn('#16a34a')}>WhatsApp</a>}
                                            <button disabled={!!busy || !sel.activity?.hasApp} title={sel.activity?.hasApp ? '' : 'Driver app is not registered for push'} onClick={() => nudge(sel)} style={{ ...btn('#fff', '#b45309', '1px solid #fcd34d'), opacity: sel.activity?.hasApp ? 1 : 0.5 }}>
                                                {busy === `${sel.key}:nudge` ? 'Sending…' : '🔔 Send app reminder'}
                                            </button>
                                        </div>
                                    </>
                                )}
                            </Section>

                            {/* Trip */}
                            <Section title="Trip & route">
                                <Grid rows={[
                                    ['Date', fmtDate(sel.date)],
                                    ['Schedule', `${sel.scheduledStart}${sel.scheduledEnd ? ' – ' + sel.scheduledEnd : ''}`],
                                    ['Client', sel.clientName || '—'],
                                    ['Route', sel.routeName || '—'],
                                    ['Pickup', sel.pickupLocation || '—'],
                                    ['Drop', sel.dropLocation || '—'],
                                    ['Type', [sel.tripType, sel.billingType, sel.frequency].filter(Boolean).join(' · ')],
                                    ['Passengers', String(sel.passengers || 0)],
                                    ['Status for this date', sel.status],
                                    ...(isAdmin && sel.operatorName ? [['Operator', sel.operatorName]] : []),
                                ]} />
                            </Section>

                            <Section title="Vehicle">
                                {sel.vehicle
                                    ? <Grid rows={[['Number', sel.vehicle.number || '—'], ['Model', sel.vehicle.model || '—'], ['Code', sel.vehicle.code || '—']]} />
                                    : <div style={{ fontSize: 13, color: '#b91c1c', fontWeight: 600 }}>No vehicle assigned</div>}
                            </Section>

                            <Section title={`Close out ${sel.isToday ? 'today' : fmtDate(sel.date)}`}>
                                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
                                    {sel.isRecurring ? 'Only this date changes. Other dates of this recurring trip stay as they are.' : 'Updates this one-time trip.'}
                                </div>
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                    <button disabled={!!busy} onClick={() => resolve(sel, 'Completed')} style={btn('#10b981')}>{busy === `${sel.key}:Completed` ? 'Saving…' : '✓ Mark as completed'}</button>
                                    <button disabled={!!busy} onClick={() => { setCancelFor(sel); setCancelReason(''); }} style={btn('#fff', RED, '1px solid #fecaca')}>✕ Cancel this trip</button>
                                </div>
                            </Section>
                        </div>
                    </div>
                </div>
            )}

            {/* Cancel reason */}
            {cancelFor && (
                <div onClick={() => setCancelFor(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
                    <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, padding: 22, width: 'min(420px, 100%)' }}>
                        <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>Cancel trip for {fmtDate(cancelFor.date)}?</div>
                        <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>{cancelFor.tripName}</div>
                        <textarea autoFocus value={cancelReason} onChange={e => setCancelReason(e.target.value)} placeholder="Reason (e.g. driver absent, client cancelled)"
                            style={{ width: '100%', minHeight: 80, marginTop: 14, padding: 10, borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit' }} />
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
                            <button onClick={() => setCancelFor(null)} style={btn('#fff', '#334155', '1px solid #e2e8f0')}>Back</button>
                            <button disabled={!cancelReason.trim() || !!busy} onClick={async () => { const r = cancelFor; setCancelFor(null); await resolve(r, 'Cancelled', cancelReason); }}
                                style={{ ...btn(RED), opacity: cancelReason.trim() ? 1 : 0.5 }}>Cancel trip</button>
                        </div>
                    </div>
                </div>
            )}

            {toast && (
                <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: '#0f172a', color: '#fff', padding: '10px 18px', borderRadius: 10, fontSize: 13, fontWeight: 600, zIndex: 1200, boxShadow: '0 8px 24px rgba(0,0,0,0.2)' }}>{toast}</div>
            )}
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #eef0f4', padding: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>{title}</div>
            {children}
        </div>
    );
}

function Grid({ rows }) {
    return (
        <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', rowGap: 8, columnGap: 12, marginTop: 10 }}>
            {rows.map(([k, v]) => (
                <React.Fragment key={k}>
                    <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>{k}</div>
                    <div style={{ fontSize: 13, color: '#0f172a', fontWeight: 600, wordBreak: 'break-word' }}>{v}</div>
                </React.Fragment>
            ))}
        </div>
    );
}
